import { useState } from 'react';
import { Dropdown, Nav, Offcanvas, Tab } from 'react-bootstrap';
import useIsMobile from '../hooks/useIsMobile';

// Replaces the old `<Nav variant="tabs" className="flex-nowrap overflow-auto">` + `<NavDropdown>`
// combo the dashboards used. Two problems with that: the `overflow-auto` scroll container clipped
// the "More" dropdown menu (it showed half-hidden / behind the content below it), and on a narrow
// screen the primary tabs just scrolled off with no affordance.
//
// Desktop: the primary tabs wrap onto a second row; the overflow ("More") menu renders in a fixed
// layer (`strategy: 'fixed'`) so no ancestor's overflow can clip it.
//
// Mobile (< md): the whole tab strip collapses. A sticky "‹ <current section>" bar stays on
// screen; tapping it opens a left off-canvas drawer listing every section (primary + more,
// flat). Picking one closes the drawer. So on a phone the dashboard is one pane at a time with a
// persistent way back to the section list, instead of a row of tabs that scrolls off.
//
//   <DashboardTabs
//     activeKey={activeTab} onSelect={setActiveTab}
//     primary={[['bookings', `Bookings (${n})`], ...]}
//     more={[['business', 'Business'], ...]}
//   >
//     <Tab.Pane eventKey="bookings">…</Tab.Pane>
//     …
//   </DashboardTabs>
export default function DashboardTabs({ activeKey, onSelect, primary = [], more = [], children }) {
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const select = (k) => { if (k) onSelect(k); };
  const activeInMore = more.some(([key]) => key === activeKey);
  const allTabs = [...primary, ...more];
  const activeLabel = (allTabs.find(([key]) => key === activeKey) || [null, 'Choose a section'])[1];

  return (
    <Tab.Container activeKey={activeKey} onSelect={select}>
      {isMobile ? (
        <>
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="btn btn-outline-secondary w-100 text-start d-flex align-items-center gap-2 mb-3 position-sticky top-0"
            style={{ zIndex: 1020 }}
            aria-label={`Current section: ${activeLabel}. Tap to change section.`}
          >
            <span aria-hidden="true">&#8249;</span>
            <span className="fw-semibold flex-grow-1 text-truncate">{activeLabel}</span>
            <span className="small text-muted">Sections</span>
          </button>
          <Offcanvas show={drawerOpen} onHide={() => setDrawerOpen(false)} placement="start">
            <Offcanvas.Header closeButton>
              <Offcanvas.Title>Dashboard sections</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="p-0">
              <Nav
                variant="pills" className="flex-column" activeKey={activeKey}
                onSelect={(k) => { select(k); setDrawerOpen(false); }}
              >
                {allTabs.map(([key, label]) => (
                  <Nav.Link key={key} eventKey={key} className="rounded-0 border-bottom px-3 py-2">
                    {label}
                  </Nav.Link>
                ))}
              </Nav>
            </Offcanvas.Body>
          </Offcanvas>
        </>
      ) : (
        <Nav variant="tabs" className="mb-3">
          {primary.map(([key, label]) => (
            <Nav.Item key={key}>
              <Nav.Link eventKey={key}>{label}</Nav.Link>
            </Nav.Item>
          ))}
          {more.length > 0 && (
            <Dropdown as={Nav.Item} align="end" onSelect={select}>
              <Dropdown.Toggle as={Nav.Link} className={activeInMore ? 'active' : undefined}>
                More
              </Dropdown.Toggle>
              <Dropdown.Menu renderOnMount popperConfig={{ strategy: 'fixed' }}>
                {more.map(([key, label]) => (
                  <Dropdown.Item key={key} eventKey={key} active={key === activeKey}>
                    {label}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          )}
        </Nav>
      )}
      <Tab.Content>{children}</Tab.Content>
    </Tab.Container>
  );
}
